import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode
} from 'react'
import useDoctorProfile from '../common/hooks/useDoctorProfile'
import { type DoctorType } from '../../../model/doctor'
import { useLocation, useNavigate } from 'react-router-dom'
import useAccountSubscription from './hooks/useAccountSubscription'
import { getDoctorsService } from '../../../services/Services'
import { useAuthState } from '../../../auth/AuthProvider'
import { useRole } from '../../../providers/RoleProvider'

export type Locale = 'en-us' | 'ar'
export type DossierPreferences = NonNullable<DoctorType['dossierPreferences']>

const DEFAULT_DOSSIER_PREFERENCES: DossierPreferences = {
  displayMode: 'TIMELINE',
  sortOrder: 'DESC'
}

interface AccountContextProps extends Omit<
  DoctorType,
  'locale' | 'dossierPreferences'
> {
  loading: boolean
  locale: Locale
  setLocale: (locale: Locale) => void
  dossierPreferences: DossierPreferences
  setDossierPreferences: (preferences: DossierPreferences) => void
}

const AccountContext = createContext<AccountContextProps | undefined>(undefined)

export const AccountProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const { uid } = useAuthState()
  const { role, doctorId, loading: roleLoading } = useRole()

  // Determine the effective owner ID:
  // - secretary: use the linked doctor's ID
  // - doctor: use own UID
  // Wait until role is resolved before determining effectiveOwnerId
  const effectiveOwnerId = roleLoading
    ? null
    : role === 'secretary'
      ? doctorId
      : (uid ?? null)

  const suppressOnboardingRedirect = roleLoading || role === 'secretary'

  const profile = useDoctorProfile({
    ownerId: effectiveOwnerId,
    suppressOnboardingRedirect
  })

  const [locale, setLocaleState] = useState<Locale>(
    () => (localStorage.getItem('locale') as Locale) || 'en-us'
  )
  const [dossierPreferences, setDossierPreferencesState] =
    useState<DossierPreferences>(DEFAULT_DOSSIER_PREFERENCES)

  // Sync locale from Firestore profile once it loads
  useEffect(() => {
    if (!profile.loading && profile.locale) {
      setLocaleState(profile.locale)
      localStorage.setItem('locale', profile.locale)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.loading])

  useEffect(() => {
    if (!profile.loading) {
      setDossierPreferencesState(
        profile.dossierPreferences ?? DEFAULT_DOSSIER_PREFERENCES
      )
    }
  }, [profile.dossierPreferences, profile.loading])

  const setLocale = useCallback(
    (newLocale: Locale) => {
      setLocaleState(newLocale)
      localStorage.setItem('locale', newLocale)
      if (effectiveOwnerId) {
        getDoctorsService().updateDoctorProfile({
          ownerId: effectiveOwnerId,
          doctorData: { locale: newLocale }
        })
      }
    },
    [effectiveOwnerId]
  )

  const setDossierPreferences = useCallback(
    (preferences: DossierPreferences) => {
      setDossierPreferencesState(preferences)
      if (effectiveOwnerId) {
        void getDoctorsService().updateDoctorProfile({
          ownerId: effectiveOwnerId,
          doctorData: { dossierPreferences: preferences }
        })
      }
    },
    [effectiveOwnerId]
  )

  return (
    <AccountContext.Provider
      value={{
        ...profile,
        locale,
        setLocale,
        dossierPreferences,
        setDossierPreferences
      }}
    >
      {children}
    </AccountContext.Provider>
  )
}

export const useAccount = () => {
  const context = useContext(AccountContext)
  const { isAuthenticated, isLoading: authLoading } = useAuthState()

  const { pathname } = useLocation()
  const navigate = useNavigate()

  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider')
  }

  const subscription = useAccountSubscription(context)
  const { isPlanExpired, isSuspended, isFreeMaxUsageReached } = subscription
  const isAccountFrozen = isPlanExpired || isSuspended

  useEffect(() => {
    if (authLoading || !isAuthenticated || context.loading) return
    if (isAccountFrozen && pathname !== '/frozen') {
      navigate('/frozen')
    } else if (isFreeMaxUsageReached && pathname === '/patients/new') {
      navigate('/dashboard')
    }
  }, [
    context.loading,
    authLoading,
    isAuthenticated,
    isAccountFrozen,
    isFreeMaxUsageReached,
    navigate,
    pathname
  ])

  return { ...context, ...subscription }
}
